import { JsonObject as CommonJsonObject } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { PusherDomainPrefix, PusherServicePrefix } from "@/domains/pusher/constants";
import { objectModule } from "@/modules/object";
import { validationModule } from "@/modules/validation";
import { AppStoreSyncStore } from "@/store/sync/AppStoreSyncStore";
import { SerializedSyncOperation, SyncOperationGeneric } from "@/store/sync/operations/types";
import { liveQuery, Subscription } from "dexie";
import { Duration } from "luxon";
import { reaction } from "mobx";
import { JsonObject } from "type-fest";

export function generateSyncActionSpaceScopedPusherChannelKey({ spaceId }: { spaceId: Uuid }) {
  const uppercaseSpaceId = spaceId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.SPACE_SCOPED_SYNC_UPDATE}@${uppercaseSpaceId}`;
}

export function generateSyncActionCollectionScopedPusherChannelKey({
  collectionId,
}: {
  collectionId: Uuid;
}) {
  const uppercaseCollectionId = collectionId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.COLLECTION_SCOPED_SYNC_UPDATE}@${uppercaseCollectionId}`;
}

export function generateSyncActionSpaceAccountScopedPusherChannelKey({
  spaceAccountId,
}: {
  spaceAccountId: Uuid;
}) {
  const uppercaseSpaceAccountId = spaceAccountId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.SPACE_ACCOUNT_SCOPED_SYNC_UPDATE}@${uppercaseSpaceAccountId}`;
}

export function generateSpaceAccountNoteLiveSyncUpdatePusherChannelKey({
  spaceAccountNoteId,
}: {
  spaceAccountNoteId: string;
}) {
  const uppercaseSpaceAccountNoteId = spaceAccountNoteId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.SPACE_ACCOUNT_NOTE_LIVE_SYNC_UPDATE}@${uppercaseSpaceAccountNoteId}`;
}

export function generateSessionLiveSyncUpdatePusherChannelKey({
  sessionId,
}: {
  sessionId: string;
}) {
  const uppercaseSessionId = sessionId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.SESSION_LIVE_SYNC_UPDATE}@${uppercaseSessionId}`;
}

export function generatePlatformInfoPusherChannelKey({ spaceAccountId }: { spaceAccountId: Uuid }) {
  const uppercaseSpaceAccountId = spaceAccountId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.PLATFORM_INFO}@${uppercaseSpaceAccountId}`;
}

export function standardizeSyncId(syncId: string | undefined | null) {
  if (!syncId) {
    return undefined;
  }

  /**
   * These are our modern "CVR-style" sync IDs.
   * We shouldn't be doing any "standardization" of them.
   */
  if (syncId.startsWith("SYNC__")) {
    return syncId;
  }

  /**
   * Legacy logic for parsing out sync event ids from pre-CVR sync updates.
   *
   * TODO: @MacroMackie follow up with this on Monday, Nov 11.
   */
  const [prefix, uuid] = syncId.split("::");
  if (prefix !== "SYNC") throw new Error(`Invalid sync id: ${syncId}`);
  if (!validationModule.isUuid(uuid)) throw new Error(`Invalid sync id: ${syncId}`);
  return `${prefix}::${uuid.toUpperCase()}`;
}

const setStringFieldIfPresent = ({
  key,
  result,
  value,
}: {
  key: string;
  result: CommonJsonObject;
  value: unknown;
}) => {
  if (value === undefined || value === null) return;
  result[key] = String(value);
};

const LOGGABLE_PAYLOAD_ID_KEYS = [
  "id",
  "note_id",
  "collection_id",
  "template_id",
  "space_id",
  "space_account_id",
  "account_id",
  "calendar_event_id",
  "audio_recording_id",
  "file_id",
  "image_id",
  "source_id",
  "sync_model_id",
];

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const getLoggablePayloadIds = (payload: unknown): CommonJsonObject => {
  if (!isRecord(payload)) return {};

  const result: CommonJsonObject = {};

  for (const key of LOGGABLE_PAYLOAD_ID_KEYS) {
    const value = payload[key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      result[key] = String(value);
    }
  }

  return result;
};

export function getLoggableOperation(operation?: SyncOperationGeneric): CommonJsonObject {
  const operationRecord = { ...operation } as unknown as Record<string, unknown>;
  const payloadIds = getLoggablePayloadIds(operationRecord.payload);
  const result: CommonJsonObject = {
    operation_kind: operation?.operationKind || "<missing>",
    payload_ids: payloadIds,
    payload: objectModule.summarizeForLogging(operationRecord.payload),
  };

  setStringFieldIfPresent({
    key: "model_id",
    result,
    value: operationRecord.modelId ?? payloadIds.id,
  });
  setStringFieldIfPresent({
    key: "note_id",
    result,
    value: payloadIds.note_id,
  });
  setStringFieldIfPresent({
    key: "collection_id",
    result,
    value: payloadIds.collection_id,
  });
  setStringFieldIfPresent({
    key: "operation_id",
    result,
    value: operationRecord.operationId,
  });
  setStringFieldIfPresent({
    key: "sync_model_id",
    result,
    value: operationRecord.syncModelId ?? payloadIds.sync_model_id,
  });

  return result;
}

export function formatSyncOperation(
  syncOperation?: SyncOperationGeneric | SerializedSyncOperation
): JsonObject | null {
  if (!syncOperation) return null;

  return {
    operation_id: `${syncOperation.operationId}`,
    operation_kind: `${syncOperation.operationKind}`,
    payload: syncOperation.payload as JsonObject,
  };
}

export function saveJsonToFile(obj: object, filename: string) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

const DEFAULT_EXTRA_WAIT = Duration.fromObject({ milliseconds: 250 });

/**
 * @param syncStore - The sync store responsible for managing live query polling.
 * @param query - A function that checks for an expected update from Dexie.
 * @param timeout - How long to wait for the sync/query, as a Duration.
 * @param extraWait - Additional buffer after the query succeeds, as a Duration.
 * @returns A promise that resolves when the query succeeds after the next sync completes.
 */
export const waitForLiveQuerySync = ({
  syncStore,
  query,
  timeout,
  extraWait = DEFAULT_EXTRA_WAIT,
}: {
  syncStore: AppStoreSyncStore;
  query: () => boolean | Promise<boolean>;
  timeout: Duration;
  extraWait?: Duration;
}) => {
  const initialLastSyncedAt = syncStore.lastSyncedAt?.toMillis() ?? 0;
  const timeoutMs = timeout.as("milliseconds");
  const extraWaitMs = extraWait.as("milliseconds");

  return new Promise<void>((resolve, reject) => {
    let hasSettled = false;
    let queryResolved = false;
    let syncResolved = false;

    let subscription: Subscription | null = null;
    let syncDisposer: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (subscription) {
        subscription.unsubscribe();
        subscription = null;
      }
      if (syncDisposer) {
        syncDisposer();
        syncDisposer = null;
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const settle = (cb: () => void) => {
      if (hasSettled) return;
      hasSettled = true;
      cleanup();
      cb();
    };

    const completeIfReady = () => {
      if (queryResolved && syncResolved) {
        settle(() => setTimeout(() => resolve(void 0), extraWaitMs));
      }
    };

    try {
      syncStore.syncQuery.forceRefetch();
    } catch (error) {
      reject(error instanceof Error ? error : new Error("Failed to refetch sync"));
      return;
    }

    subscription = liveQuery(query).subscribe({
      next: result => {
        if (result) {
          queryResolved = true;
          subscription?.unsubscribe();
          completeIfReady();
        }
      },
      error: error => {
        settle(() => reject(error));
      },
      complete: () => {
        settle(() => reject(new Error("Live query completed without returning a result")));
      },
    });

    syncDisposer = reaction(
      () => syncStore.lastSyncedAt?.toMillis() ?? 0,
      lastSyncedAtMillis => {
        if (!syncResolved && lastSyncedAtMillis > initialLastSyncedAt) {
          syncResolved = true;
          completeIfReady();
        }
      },
      { fireImmediately: true }
    );

    timeoutId = setTimeout(() => {
      settle(() => reject(new Error("Live query timed out")));
    }, timeoutMs);
  });
};

/**
 * Waits until a sync operation has been acknowledged and moved from processing to pending.
 */
export const waitForSyncOperationToMoveToPending = ({
  syncStore,
  operationId,
  timeout,
}: {
  syncStore: AppStoreSyncStore;
  operationId: Uuid;
  timeout: Duration;
}) => {
  const timeoutMs = timeout.as("milliseconds");
  const getOperationState = async () => {
    const [processingOperation, pendingOperation] = await Promise.all([
      syncStore.actionQueue.db.queue.processing.get(operationId),
      syncStore.actionQueue.db.queue.pending.get(operationId),
    ]);

    return {
      isInProcessing: Boolean(processingOperation),
      isInPending: Boolean(pendingOperation),
    };
  };

  return new Promise<void>((resolve, reject) => {
    let hasSettled = false;
    let subscription: Subscription | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (subscription) {
        subscription.unsubscribe();
        subscription = null;
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const settle = (cb: () => void) => {
      if (hasSettled) return;
      hasSettled = true;
      cleanup();
      cb();
    };

    void (async () => {
      try {
        const initialState = await getOperationState();
        if (initialState.isInPending) {
          settle(() => resolve(void 0));
          return;
        }
        if (!initialState.isInProcessing) {
          settle(() => resolve(void 0));
          return;
        }
        if (hasSettled) return;

        subscription = liveQuery(getOperationState).subscribe({
          next: state => {
            if (state.isInPending) {
              settle(() => resolve(void 0));
            }
          },
          error: error => {
            settle(() => reject(error));
          },
          complete: () => {
            settle(() =>
              reject(new Error("Sync operation watcher completed without pending acknowledgment"))
            );
          },
        });

        timeoutId = setTimeout(() => {
          settle(() =>
            reject(
              new Error(`Timed out waiting for sync operation ${operationId} to reach pending`)
            )
          );
        }, timeoutMs);
      } catch (error) {
        settle(() =>
          reject(error instanceof Error ? error : new Error("Failed to read sync operation state"))
        );
      }
    })();
  });
};
